import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import {
  IMultipleChoiceOption,
  correctMascotPNG,
  correctMascotWEBP,
  incorrectMascotPNG,
  incorrectMascotWEBP,
} from '~/pages/anatomyV2/libs/components/MultipleChoiceQuestionTemplate/constants'
import { MultipleChoiceQuestionTemplateStyles as S } from '~/pages/anatomyV2/libs/components/MultipleChoiceQuestionTemplate/styles'
import SectionProgress from '~/pages/anatomyV2/libs/components/SectionProgress'
import { useMatchHeaderBackground } from '~/pages/anatomyV2/libs/hooks/useMatchHeaderBackground'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { TPageProps } from '~/shared/types/commonTypes'

interface IMultipleChoiceQuestionTemplateProps extends TPageProps {
  currentSection: number
  titleI18nKey: string
  options: IMultipleChoiceOption[]
  correctOptionKey: string
  correctExplanationI18nKey: string
  incorrectTitleI18nKey: string
  incorrectExplanationI18nKey: string
  question: string
}

const MultipleChoiceQuestionTemplate = ({
  pageId,
  nextPagePath,
  currentSection,
  titleI18nKey,
  options,
  correctOptionKey,
  correctExplanationI18nKey,
  incorrectTitleI18nKey,
  incorrectExplanationI18nKey,
  question,
}: IMultipleChoiceQuestionTemplateProps) => {
  useMatchHeaderBackground()

  const { t } = useTranslation('anatomyV2')
  const [answer, setAnswer] = useState<string | null>(null)

  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question,
  })

  const isCorrect = answer === correctOptionKey

  return (
    <S.Root>
      <S.ContentWrapper>
        <SectionProgress currentSection={currentSection} pageId={pageId} />

        <S.Title>
          <Trans ns="anatomyV2" i18nKey={titleI18nKey} />
        </S.Title>

        {answer === null ? (
          <S.Grid>
            {options.map(({ key, i18nKey, png, webp }) => (
              <S.OptionCard
                key={key}
                type="button"
                onClick={() => setAnswer(key)}
              >
                <S.CardImage>
                  <picture>
                    <source srcSet={webp} type="image/webp" />
                    <img src={png} alt={t(i18nKey)} />
                  </picture>
                </S.CardImage>

                <S.CardLabel>{t(i18nKey)}</S.CardLabel>
              </S.OptionCard>
            ))}
          </S.Grid>
        ) : (
          <>
            <S.ResultContent>
              <S.ResultCard $isCorrect={isCorrect}>
                <picture>
                  <source
                    srcSet={isCorrect ? correctMascotWEBP : incorrectMascotWEBP}
                    type="image/webp"
                  />
                  <S.ResultMascot
                    src={isCorrect ? correctMascotPNG : incorrectMascotPNG}
                    alt=""
                  />
                </picture>

                <S.ResultTitle $isCorrect={isCorrect}>
                  <Trans
                    ns="anatomyV2"
                    i18nKey={
                      isCorrect
                        ? 'onboarding.multipleChoice.correct'
                        : incorrectTitleI18nKey
                    }
                  />
                </S.ResultTitle>

                <S.ResultExplanation>
                  <Trans
                    ns="anatomyV2"
                    i18nKey={
                      isCorrect
                        ? correctExplanationI18nKey
                        : incorrectExplanationI18nKey
                    }
                  />
                </S.ResultExplanation>
              </S.ResultCard>
            </S.ResultContent>

            <S.StyledButton
              type="button"
              onClick={() => handleContinue(answer)}
            >
              <Trans i18nKey="actions.continue" />
            </S.StyledButton>
          </>
        )}
      </S.ContentWrapper>
    </S.Root>
  )
}

export default MultipleChoiceQuestionTemplate
